import type {
  APIKey,
  ScienceLLM,
  UsageRecord,
  UsageSummaryItem,
  AdaptedAPIKey,
  AdaptedModel,
  AdaptedUsageLog,
  AdaptedUsageChart,
} from "./types";

// =============================================================================
// Helpers
// =============================================================================

function formatRelativeTime(isoString: string | undefined): string {
  if (!isoString) return "Never";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hr ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  return date.toLocaleDateString();
}

function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function formatDate(isoString: string): string {
  return isoString.split("T")[0];
}

// =============================================================================
// API Key adapter
// =============================================================================

export function adaptApiKey(key: APIKey): AdaptedAPIKey {
  // Revoked takes precedence over expired: the backend stores both fields
  // independently and ValidateByKey rejects either at proxy time, so this
  // derived label is purely cosmetic — revocation is the more useful signal
  // when both flags are set.
  let status: AdaptedAPIKey["status"] = "active";
  if (key.revoked_at) {
    status = "revoked";
  } else if (key.expires_at && new Date(key.expires_at).getTime() <= Date.now()) {
    status = "expired";
  }
  return {
    id: key.id,
    name: key.name,
    nameEn: key.name,
    key: key.key_prefix,
    created: formatDate(key.created_at),
    lastUsed: formatRelativeTime(key.last_used_at),
    monthlyUsage: 0, // backend list doesn't include per-key usage
    limit: null,
    status,
    isLegacy: key.has_ciphertext === false,
  };
}

// =============================================================================
// Model adapter — the public listing returns ScienceLLM (product) rows.
// Pricing lives on the UpstreamModels behind it, not on the product, so the
// row card shows capacity but no per-Ktok rate.
// =============================================================================

export function adaptModel(sl: ScienceLLM): AdaptedModel {
  return {
    id: sl.model_key,
    name: sl.name,
    provider: "ScienceLLM",
    providerShort: "SL",
    tier: "Standard",
    contextLength: sl.context_window,
    inputPrice: 0,
    outputPrice: 0,
    rpmLimit: sl.rpm_limit ?? null,
    tpmLimit: sl.tpm_limit ?? null,
    tags: [],
    description: sl.description || "",
    descriptionEn: sl.description || "",
    capabilities: [],
    status: sl.is_active === false ? "unavailable" : "available",
    isNew: false,
    isFeatured: false,
    protocol: sl.protocol,
  };
}

// =============================================================================
// Usage Record adapter
// =============================================================================

export function adaptUsageRecord(
  record: UsageRecord,
  apiKeyMap: Record<string, string> = {}
): AdaptedUsageLog {
  return {
    id: record.id,
    requestId: record.id,
    model: record.science_llm_key || record.model_key || "—",
    upstreamModel: record.model_key && record.model_key !== record.science_llm_key ? record.model_key : undefined,
    provider: record.provider,
    type: "llm",
    status:
      record.status === "success"
        ? "success"
        : record.status === "pending"
          ? "pending"
          : "failed",
    timestamp: formatTimestamp(record.started_at),
    tokens: record.total_tokens,
    inputTokens: record.input_tokens,
    outputTokens: record.output_tokens,
    cacheReadTokens: record.cache_read_tokens,
    cacheWriteTokens: record.cache_write_tokens,
    latency: record.latency_ms || 0,
    cost: Number(record.actual_amount).toLocaleString(undefined, { maximumFractionDigits: 4 }),
    keyName: apiKeyMap[record.api_key_id || ""] || "—",
    errorMessage: record.error_code || undefined,
    errorDetail: record.error_detail || undefined,
    userId: record.user_id,
    teamId: record.team_id,
  };
}

// =============================================================================
// Usage Chart adapter
// =============================================================================

export function adaptUsageChart(
  items: UsageSummaryItem[]
): AdaptedUsageChart[] {
  return items.map((item) => ({
    date: item.group_key,
    llm: item.input_tokens + item.output_tokens,
  }));
}
